import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Tag,
  Button,
  Space,
  Select,
  Typography,
  Tooltip,
  Modal,
  InputNumber,
  Form,
  Alert,
  Descriptions,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { hasPermission } from '../../utils/permissions';
import {
  fetchCreditNotesRequest,
  adjustCreditNoteRequest,
} from '../../redux/actions/returnOrderActions';

const { Text } = Typography;
const { Option } = Select;

const fmtMoney = (v) => (v != null ? `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—');
const fmtDate  = (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—');

export const getCreditNoteStatusTag = (status) => {
  const map = {
    received: { color: 'blue',    icon: null,                      text: 'Received' },
    adjusted: { color: 'success', icon: <CheckCircleOutlined />,   text: 'Adjusted' },
    cancelled:{ color: 'error',   icon: <CloseCircleOutlined />,   text: 'Cancelled'},
  };
  const cfg = map[status] || { color: 'default', icon: null, text: status || '—' };
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ fontWeight: 500 }}>
      {cfg.text}
    </Tag>
  );
};

/**
 * CreditNoteList
 *
 * Props:
 *  vendorFilter — optional uint (for vendor-specific view)
 */
const CreditNoteList = ({ vendorFilter: externalVendorFilter }) => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { creditNotes, loading, actionLoading } = useSelector((s) => s.returns);

  const canAdjust = hasPermission(permissions, 'bill_receipt/verify');

  const [statusFilter, setStatusFilter] = useState('');
  const [adjustModal, setAdjustModal]   = useState(null); // { id, cnNumber, vendorName, amount }
  const [billId, setBillId]             = useState('');
  const [billIdError, setBillIdError]   = useState('');

  const loadList = () => {
    const params = {};
    if (externalVendorFilter) params.vendor_id = externalVendorFilter;
    if (statusFilter)         params.status    = statusFilter;
    dispatch(fetchCreditNotesRequest(params));
  };

  const handleAdjust = () => {
    const id = parseInt(billId, 10);
    if (!id || id <= 0) { setBillIdError('Enter a valid Bill Receipt ID'); return; }
    dispatch(adjustCreditNoteRequest(adjustModal.id, id));
    setAdjustModal(null);
    setBillId('');
    setBillIdError('');
  };

  // Stats for this view
  const totalReceived    = creditNotes.filter((c) => c.status === 'received').reduce((s, c) => s + (c.amount || 0), 0);
  const totalAdjusted    = creditNotes.filter((c) => c.status === 'adjusted').reduce((s, c) => s + (c.amount || 0), 0);
  const pendingCount     = creditNotes.filter((c) => c.status === 'received').length;

  const columns = [
    {
      title: 'Credit Note #',
      dataIndex: 'credit_note_number',
      key: 'credit_note_number',
      width: 160,
      render: (v) => <Text style={{ color: '#52c41a', fontWeight: 600 }}>{v}</Text>,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 160,
      ellipsis: true,
      render: (v) => <Tooltip title={v}><span>{v}</span></Tooltip>,
    },
    {
      title: 'Return Order',
      dataIndex: 'return_order_id',
      key: 'return_order_id',
      width: 120,
      render: (v) => <Text style={{ color: '#fa8c16' }}>RET #{v}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'credit_note_date',
      key: 'credit_note_date',
      width: 110,
      render: fmtDate,
      sorter: (a, b) => new Date(a.credit_note_date) - new Date(b.credit_note_date),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (v) => <Text strong style={{ color: '#52c41a', fontSize: 14 }}>{fmtMoney(v)}</Text>,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: getCreditNoteStatusTag,
    },
    {
      title: 'Adjusted Against',
      dataIndex: 'adjusted_against_bill_id',
      key: 'adjusted_against_bill_id',
      width: 140,
      render: (v, r) =>
        v ? (
          <Space>
            <Tag color="success">Bill #{v}</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(r.adjusted_at)}</Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Received By',
      dataIndex: 'received_by_name',
      key: 'received_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) =>
        canAdjust && record.status === 'received' ? (
          <Button
            size="small"
            type="primary"
            ghost
            icon={<SwapOutlined />}
            onClick={() =>
              setAdjustModal({
                id: record.id,
                cnNumber: record.credit_note_number,
                vendorName: record.vendor_name,
                amount: record.amount,
              })
            }
          >
            Adjust
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      {/* Summary */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}>
          <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
            <Statistic title="Pending Adjustment" value={pendingCount}
              valueStyle={{ color: pendingCount > 0 ? '#fa8c16' : '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
            <Statistic title="Outstanding Credit"
              value={Number(totalReceived).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              prefix="₹" valueStyle={{ color: '#fa8c16', fontSize: 16 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
            <Statistic title="Total Adjusted"
              value={Number(totalAdjusted).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              prefix="₹" valueStyle={{ color: '#52c41a', fontSize: 16 }} />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Space wrap>
          <Select
            placeholder="Filter by status"
            style={{ width: 160 }}
            allowClear
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v || '')}
          >
            <Option value="received">🔵 Received</Option>
            <Option value="adjusted">🟢 Adjusted</Option>
            <Option value="cancelled">🔴 Cancelled</Option>
          </Select>
          <Button type="primary" onClick={loadList} icon={<ReloadOutlined />} loading={loading}>
            Filter
          </Button>
          <Button onClick={() => { setStatusFilter(''); dispatch(fetchCreditNotesRequest({})); }}>
            Clear
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={creditNotes}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        rowClassName={(r) => r.status === 'received' ? 'cn-row-pending' : ''}
        locale={{
          emptyText: (
            <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
              No credit notes found. Credit notes are recorded when return orders are completed.
            </div>
          ),
        }}
      />

      {/* Adjust Modal */}
      <Modal
        title={
          <Space>
            <SwapOutlined style={{ color: '#52c41a' }} />
            Adjust Credit Note — {adjustModal?.cnNumber}
          </Space>
        }
        open={!!adjustModal}
        onCancel={() => { setAdjustModal(null); setBillId(''); setBillIdError(''); }}
        footer={
          <Space>
            <Button onClick={() => { setAdjustModal(null); setBillId(''); setBillIdError(''); }}>Cancel</Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={actionLoading}
              onClick={handleAdjust}
            >
              Adjust Against Bill
            </Button>
          </Space>
        }
        destroyOnClose
      >
        {adjustModal && (
          <>
            <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Vendor">{adjustModal.vendorName}</Descriptions.Item>
              <Descriptions.Item label="Credit Amount">
                <Text strong style={{ color: '#52c41a', fontSize: 16 }}>{fmtMoney(adjustModal.amount)}</Text>
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              message="Enter the Bill Receipt ID against which this credit will be offset."
              style={{ marginBottom: 12 }}
            />
            <div>
              <Text style={{ fontSize: 13 }}>Bill Receipt ID</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 6 }}
                min={1}
                precision={0}
                placeholder="Enter Bill Receipt ID"
                value={billId || undefined}
                onChange={(val) => { setBillId(val); setBillIdError(''); }}
                status={billIdError ? 'error' : ''}
              />
              {billIdError && (
                <Text type="danger" style={{ fontSize: 12 }}>{billIdError}</Text>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CreditNoteList;
